import React, { useState, useEffect } from 'react';
import {
  FiSearch, FiRefreshCw, FiCheckCircle, FiXCircle, FiSmartphone, FiClock
} from 'react-icons/fi';
import { superAdminAPI } from '../../services/api';
import DataTable from '../../components/shared/DataTable';

const SmsLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await superAdminAPI.getSmsLogs({ search, status: statusFilter, limit: 100 });
      setLogs(res.data?.logs || []);
      setTotal(res.data?.total || 0);
    } catch (error) {
      console.error('Erreur chargement logs SMS:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [statusFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const columns = [
    {
      header: 'Agence',
      render: r => (
        <div>
          <strong style={{ color: 'var(--text-primary)' }}>{r.agency_name || 'Système / Plateforme'}</strong>
          <div style={{ fontSize: 11, color: '#64748b' }}>Type: <code>{r.sms_type}</code></div>
        </div>
      )
    },
    {
      header: 'Destinataire',
      render: r => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.recipient_name || 'Locataire'}</div>
          <div style={{ fontSize: 12, color: '#2563eb', fontWeight: 700 }}>
            📞 {r.recipient_phone}
          </div>
        </div>
      )
    },
    {
      header: 'Message SMS',
      render: r => (
        <div style={{ maxWidth: 380, fontSize: 12, lineHeight: 1.4 }}>
          {r.message}
          {r.error_message && (
            <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>
              Erreur: {r.error_message}
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Solde Agence',
      render: r => (
        <div style={{ fontSize: 12 }}>
          {r.agency_credits_before !== null ? (
            <span>{r.agency_credits_before} → <strong style={{ color: '#7c3aed' }}>{r.agency_credits_after}</strong></span>
          ) : '—'}
        </div>
      )
    },
    {
      header: 'Statut BatakiPlus',
      render: r => {
        let badge = 'success';
        let label = '✅ Délivré';
        if (r.status === 'simulated') {
          badge = 'info';
          label = '📱 Simulé';
        } else if (r.status === 'failed') {
          badge = 'danger';
          label = '❌ Échec';
        }

        return (
          <div>
            <span className={`badge badge-${badge}`} style={{ fontWeight: 700 }}>
              {label}
            </span>
            {r.bataki_remaining_credits !== null && (
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                Master: {r.bataki_remaining_credits} restants
              </div>
            )}
          </div>
        );
      }
    },
    {
      header: 'Date & Heure',
      render: r => (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {fmtDate(r.created_at)}
        </div>
      )
    }
  ];

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0 }}>Journal d'Audit des SMS (Passerelle BatakiPlus)</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Traçabilité intégrale de toutes les notifications et alertes SMS envoyées aux locataires
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={fetchLogs}>
            <FiRefreshCw style={{ marginRight: 4 }} /> Actualiser
          </button>
        </div>
      </div>

      {/* Barre de recherche & Filtres */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ padding: 16 }}>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 260, position: 'relative' }}>
              <FiSearch style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-secondary)' }} />
              <input
                type="text"
                className="form-control"
                style={{ paddingLeft: 36 }}
                placeholder="Rechercher par destinataire, numéro de téléphone, texte ou agence..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              className="form-control"
              style={{ width: 160 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="sent">Délivrés (Succès)</option>
              <option value="simulated">Simulés</option>
              <option value="failed">Échoués</option>
            </select>

            <button type="submit" className="btn btn-primary">
              Filtrer
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setSearch(''); setStatusFilter(''); }}
            >
              Réinitialiser
            </button>
          </form>
        </div>
      </div>

      {/* Tableau des logs */}
      <div className="card">
        <div className="card-header">
          <h2>Historique des envois ({logs.length} sur {total} logs)</h2>
        </div>
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <DataTable columns={columns} data={logs} emptyMessage="Aucun log SMS enregistré." />
        )}
      </div>
    </div>
  );
};

export default SmsLogsPage;
